/**
 * Playful sound effects using Web Audio API — no files needed.
 * Designed for kids: video-game-style, bright, cartoonish.
 * Respects localStorage 'soundEnabled' pref (default: on).
 */

let audioCtx = null;

const getCtx = () => {
  if (audioCtx) return audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  audioCtx = new Ctx();
  return audioCtx;
};

const isEnabled = () => localStorage.getItem('soundEnabled') !== 'false';

/** Fixed-pitch tone with smooth attack/release envelope. */
const playTone = (freq, duration, startTime, type = 'square', volume = 0.18) => {
  const ctx = getCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.connect(gain);
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(volume, startTime + 0.006);
  gain.gain.linearRampToValueAtTime(volume, startTime + duration - 0.02);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);
  osc.start(startTime);
  osc.stop(startTime + duration);
};

/** Pitch-slide tone — exponential ramp from freqStart → freqEnd. */
const playSweep = (freqStart, freqEnd, duration, startTime, type = 'square', volume = 0.18) => {
  const ctx = getCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freqStart, startTime);
  osc.frequency.exponentialRampToValueAtTime(Math.max(freqEnd, 20), startTime + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(volume, startTime + 0.006);
  gain.gain.linearRampToValueAtTime(volume, startTime + duration - 0.03);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);
  osc.start(startTime);
  osc.stop(startTime + duration);
};

const ensureReady = () => {
  if (!isEnabled()) return null;
  const ctx = getCtx();
  if (!ctx) return null;
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
};

/** ✅ Correct — video-game power-up: rapid rising arpeggio + sparkle top note. */
export const playCorrect = () => {
  const ctx = ensureReady();
  if (!ctx) return;
  const t = ctx.currentTime;
  // Quick ascending arpeggio (square wave = classic game feel)
  playTone(659.25,  0.07, t,        'square',   0.18);  // E5
  playTone(987.77,  0.07, t + 0.07, 'square',   0.18);  // B5
  playTone(1318.51, 0.07, t + 0.14, 'square',   0.18);  // E6
  // Final sparkle with triangle for warmth
  playTone(1975.53, 0.22, t + 0.21, 'triangle', 0.22);  // B6 held
};

/** ❌ Incorrect — cartoon trombone slide: quick descending pitch bend (not harsh). */
export const playIncorrect = () => {
  const ctx = ensureReady();
  if (!ctx) return;
  const t = ctx.currentTime;
  // "Wah-wahh" cartoon slide from A4 → A3
  playSweep(440, 220, 0.35, t, 'square', 0.18);
  // Soft chirp at start for texture
  playTone(466.16, 0.08, t, 'triangle', 0.10);
};

/** 🎉 Complete — full fanfare: fast arpeggio then held major chord. */
export const playComplete = () => {
  const ctx = ensureReady();
  if (!ctx) return;
  const t = ctx.currentTime;
  // Fast C-major arpeggio climbing 2 octaves
  playTone(523.25,  0.06, t,         'square', 0.18);   // C5
  playTone(659.25,  0.06, t + 0.06,  'square', 0.18);   // E5
  playTone(783.99,  0.06, t + 0.12,  'square', 0.18);   // G5
  playTone(1046.50, 0.06, t + 0.18,  'square', 0.18);   // C6
  playTone(1318.51, 0.06, t + 0.24,  'square', 0.18);   // E6
  playTone(1567.98, 0.06, t + 0.30,  'square', 0.18);   // G6
  // Final held chord stack (C + E + G) — fanfare
  playTone(1046.50, 0.40, t + 0.36, 'triangle', 0.22);  // C6
  playTone(1318.51, 0.40, t + 0.36, 'triangle', 0.18);  // E6
  playTone(1567.98, 0.40, t + 0.36, 'triangle', 0.15);  // G6
};

/** 🔔 Optional — click feedback for UI taps (short, bright). */
export const playClick = () => {
  const ctx = ensureReady();
  if (!ctx) return;
  playTone(880, 0.04, ctx.currentTime, 'square', 0.10);  // A5 quick blip
};

export const setSoundEnabled = (enabled) => {
  localStorage.setItem('soundEnabled', enabled ? 'true' : 'false');
};

export const isSoundEnabled = isEnabled;
